'use client';

import { useEffect, useState } from 'react';
import ReactCardFlip from 'react-card-flip';
import { SvgArrow } from '@icons/designSystemIcons/SvgArrow';
import { SvgCross } from '@icons/designSystemIcons/SvgCross';
import { SvgHeart } from '@icons/designSystemIcons/SvgHeart';
import { SvgInfoCircle } from '@icons/designSystemIcons/SvgInfoCircle';
import { SvgSunGlow } from '@icons/designSystemIcons/SvgSunGlow';
import { getPackMessage, PacksConfigured } from '@utils/packUtils';
import { priceFormat } from '@utils/priceFormat';
import { Quantifier } from 'app/(dashboard)/dashboard/(pages)/budgets/HighLightedProduct/Quantifier';
import {
  Operation,
  useCartStore,
} from 'app/(dashboard)/dashboard/(pages)/budgets/stores/userCartStore';
import DynamicIcon, {
  IconFamily,
} from 'app/(web)/components/common/DynamicIcon';
import { useAddProductHandler } from 'app/hooks/useAddProductHandler';
import { useSessionStore } from 'app/stores/globalStore';
import { CartItem, Product } from 'app/types/product';
import { getDiscountedPrice } from 'app/utils/productCommon';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'designSystem/Accordion/Accordion';
import { Button } from 'designSystem/Buttons/Buttons';
import { Flex } from 'designSystem/Layouts/Layouts';
import { Text } from 'designSystem/Texts/Texts';
import { isEmpty } from 'lodash';

import { SESSION_PRODUCTS_EXTRA_INFO } from './ProductSessionPriceCard';

function ProductPriceItemsCard({
  isDashboard = false,
  product,
}: {
  isDashboard: boolean;
  product: Product;
}) {
  const { addItemToCart, getQuantityOfProduct, removeSingleProduct } =
    useCartStore(state => state);
  const { cleanCheckoutSession } = useSessionStore(state => state);

  const addProductHandler = useAddProductHandler({ isDashboard });

  return (
    <>
      <Flex layout="col-left" className="w-full">
        {!SESSION_PRODUCTS_EXTRA_INFO.filter(item => item.id === product.id)[0]
          ?.bullets && !isEmpty(product.appliedProducts)
          ? product.appliedProducts.map(item => {
              const iconName = item.icon.split('/')[0] || 'SvgCross';
              const iconFamily = (item.icon.split('/')[1] ||
                'designSystemIcons') as IconFamily;

              return (
                <Flex key={item.titlte} className="items-start mb-2 text-sm">
                  <DynamicIcon
                    className="mr-2 mt-0.5 text-hg-secondary shrink-0 h-4 w-4"
                    name={iconName}
                    family={iconFamily}
                  />
                  <Text>{item.titlte}</Text>
                </Flex>
              );
            })
          : !SESSION_PRODUCTS_EXTRA_INFO.filter(
              item => item.id === product.id
            )[0]?.bullets && (
              <Flex className="items-start mb-2">
                <Text>{product.description}</Text>
              </Flex>
            )}

        {SESSION_PRODUCTS_EXTRA_INFO.filter(item => item.id === product.id)[0]
          ?.bullets && (
          <ul className="text-hg-black500 text-sm mb-12 flex flex-col gap-2">
            {SESSION_PRODUCTS_EXTRA_INFO.filter(
              item => item.id === product.id
            )[0]?.bullets?.map(bullet => (
              <li key={bullet} className="flex w-full gap-2 justify-sta">
                <div className="h-1 w-1 shrink-0 bg-hg-black400 rounded-full mt-2" />
                <p
                  className="text-start"
                  dangerouslySetInnerHTML={{ __html: bullet }}
                />
              </li>
            ))}
          </ul>
        )}

        {product?.packMoreInformation && (
          <Accordion>
            <AccordionItem value="accordion">
              <AccordionContent>
                <p className="pt-3 pb-0 text-sm md:text-md">
                  {product?.packMoreInformation}
                </p>
              </AccordionContent>
              <AccordionTrigger>
                <span className="text-hg-secondary block text-left text-sm md:text-md font-semibold">
                  + info
                </span>
              </AccordionTrigger>
            </AccordionItem>
          </Accordion>
        )}
      </Flex>

      <div className="mt-auto">
        {!isDashboard && (
          <Button
            type="primary"
            className="mt-4"
            onClick={() => {
              cleanCheckoutSession();
              addProductHandler(product);
            }}
            id="tmevent_click_book_button_prices"
          >
            Me interesa
            <SvgArrow
              height={16}
              width={16}
              className="ml-2 pointer-events-none"
            />
          </Button>
        )}

        {isDashboard && (
          <div className="pt-1 mt-2">
            <Quantifier
              handleUpdateQuantity={function handleUpdateQuantity(
                operation: Operation
              ): void {
                if (operation == 'increase') {
                  addItemToCart(product as CartItem);
                } else {
                  removeSingleProduct(product as CartItem);
                }
              }}
              quantity={getQuantityOfProduct(product)}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default function ProductPriceCard({
  product,
  isDashboard = false,
  className,
  index,
}: {
  product: Product;
  parentProduct: Product;
  isDashboard?: boolean;
  className?: string;
  index: number;
}) {
  const ProductFlowwwIds = () => {
    return PacksConfigured.filter(
      pack => pack.packId === product.flowwwId.toString()
    )[0]?.productId;
  };

  const { currentOrigin } = useSessionStore(state => state);
  const [cardHeight, setCardHeight] = useState(0);
  const [isFlipped, setIsFlipped] = useState<boolean>(false);
  const [packMessage, setPackMessage] = useState<string>('');
  const [discountedPrice, setDiscountedPrice] = useState<null | number>(null);

  useEffect(() => {
    if (product && !isEmpty(product.discounts)) {
      setDiscountedPrice(getDiscountedPrice(product, currentOrigin));
    }

    if (product?.flowwwId && product.isPack) {
      setPackMessage(getPackMessage(ProductFlowwwIds()));
    }
  }, [product]);

  useEffect(() => {
    const frontPriceCard = document.getElementById(`frontPriceCard-${index}`);

    if (frontPriceCard) {
      setCardHeight(frontPriceCard.clientHeight);
    }
  }, []);

  return (
    <ReactCardFlip
      isFlipped={isFlipped}
      flipDirection="horizontal"
      containerClassName="w-full md:w-1/2 relative"
    >
      <Flex
        id={`frontPriceCard-${index}`}
        className={`bg-white h-full flex-col p-6 rounded-2xl shadow-centered-secondary ${className}`}
      >
        <Flex layout="col-left" className="w-full">
          <Flex layout="row-between" className="w-full mb-2 items-start">
            <Flex className="text-hg-secondary">
              <span className="text-2xl font-semibold md:text-2xl mr-2">
                {priceFormat(discountedPrice ? discountedPrice : product.price)}{' '}
                €
              </span>
              {discountedPrice && (
                <span className="text-l inline-block line-through font-normal text-hg-black500">
                  {priceFormat(product.price)} €
                </span>
              )}
            </Flex>

            <Flex layout="row-right" className="gap-2">
              {!isEmpty(product.tags) &&
                product.tags[0].tag &&
                product.tags[0].tag === 'Black Friday' && (
                  <Flex
                    layout="row-center"
                    className="bg-hg-black text-hg-secondary rounded-full p-1 px-2"
                  >
                    <SvgSunGlow
                      height={12}
                      width={12}
                      className="text-hg-primary mr-1"
                    />
                    <Text size="xs">
                      Black
                      <span className="font-semibold font-gtUltra text-hg-secondary500">
                        Friday
                      </span>
                      <Text size="xs">{product.tags[0].tag}</Text>
                    </Text>
                  </Flex>
                )}
              {!isEmpty(product.tags) &&
                product.tags[0].tag &&
                product.tags[0].tag === 'San Valentín' && (
                  <Button
                    key={'San Valentín'}
                    type="white"
                    className="pointer-events-none cursor-auto "
                    customStyles="border-none pl-1 bg-hg-red"
                  >
                    <SvgHeart className="ml-2" />
                    <Text className="text-xs text-hg-skin ml-2 text-bold">
                      San Valentín
                    </Text>
                  </Button>
                )}

              {packMessage && product.isPack && (
                <div
                  className=" bg-derma-secondary300 rounded-full p-2 text-hg-secondary shrink-0 aspect-square flex justify-center items-center cursor-pointer"
                  onClick={() => setIsFlipped(!isFlipped)}
                >
                  <SvgInfoCircle className="h-6 w-6" />
                </div>
              )}
            </Flex>
          </Flex>
          <Text className="font-semibold md:text-lg">{product.title}</Text>
        </Flex>
        <Flex layout="col-left" className="w-full items-start pt-2">
          <ProductPriceItemsCard isDashboard={isDashboard} product={product} />
        </Flex>
      </Flex>
      <div className="h-full flex">
        {packMessage && (
          <div
            className="bg-hg-secondary text-white rounded-2xl  flex flex-col items-start gap-4 p-6"
            style={{ minHeight: `${cardHeight}px`, height: `${cardHeight}px` }}
          >
            <div
              className="bg-white rounded-full p-2 text-hg-secondary shrink-0 aspect-square flex justify-center items-center self-end cursor-pointer"
              onClick={() => setIsFlipped(!isFlipped)}
            >
              <SvgCross className="h-4 w-4" />
            </div>

            <p className="md:text-lg font-semibold mb-auto">{packMessage}</p>

            <Button
              type="white"
              onClick={() => setIsFlipped(!isFlipped)}
              customStyles="border-0 text-hg-secondary"
            >
              <SvgArrow className="rotate-180 h-4 w-4 mr-2" />
              Volver
            </Button>
          </div>
        )}
      </div>
    </ReactCardFlip>
  );
}
